<template>
  <div class="beprince-id-card-report">
    <!-- Control Bar -->
    <div class="no-print control-bar">
      <div class="d-flex align-items-center">
        <div class="brand-badge mr-2">
          <feather-icon icon="CreditCardIcon" size="20" class="text-white" />
        </div>
        <h4 class="mb-0 font-weight-bolder">
          Be Prince Kids School
          <span class="control-bar__accent">| Student ID Cards</span>
        </h4>
        <b-badge
          v-show="!loading"
          class="ml-2 px-1 control-bar__count-badge"
        >
          {{ reportItems.length }} Students
        </b-badge>
      </div>
      <div class="action-buttons">
        <button class="btn-modern btn-print" @click="print">
          <feather-icon icon="PrinterIcon" size="18" class="mr-50" />
          <span>Print All Cards</span>
        </button>
        <button class="btn-modern btn-close" @click="close">
          <feather-icon icon="XIcon" size="18" class="mr-50" />
          <span>Exit</span>
        </button>
      </div>
    </div>

    <!-- Loading -->
    <div v-if="loading" class="text-center py-5 state-wrapper">
      <b-spinner
        variant="danger"
        style="width: 3.5rem; height: 3.5rem"
        class="mb-3"
      />
      <h4 class="font-weight-bold text-danger">Loading ID cards…</h4>
    </div>

    <!-- Error -->
    <div v-else-if="error" class="text-center py-5 state-wrapper">
      <feather-icon icon="AlertTriangleIcon" size="48" class="text-danger mb-2" />
      <h5 class="text-danger font-weight-bold">{{ error }}</h5>
      <b-button variant="outline-danger" class="mt-2" @click="loadReportData">
        Retry
      </b-button>
    </div>

    <!-- Cards -->
    <div v-else>
      <div v-if="reportItems.length === 0" class="text-center py-5 state-wrapper">
        <feather-icon icon="InboxIcon" size="64" class="text-muted mb-2" />
        <h4 class="font-weight-bold">No Students Found</h4>
      </div>

      <div v-else class="print-pages">
        <div
          v-for="(page, pageIndex) in cardPages"
          :key="'page-' + pageIndex"
          class="print-page"
        >
          <div class="cards-grid">
            <div
              v-for="(student, index) in page"
              :key="student.id || student.grNo || `${pageIndex}-${index}`"
              class="student-id-block"
            >
              <!-- Front -->
              <div class="id-card id-card--front">
              <div class="id-card__top-line"></div>

              <!-- Header -->
              <header class="id-card__header">
                <div class="id-card__header-left">
                  <svg
                    class="id-card__tagline"
                    viewBox="0 0 220 28"
                    aria-hidden="true"
                  >
                    <defs>
                      <path
                        :id="'beprince-curve-' + (student.id || index)"
                        d="M 0 22 Q 72 4 168 22"
                        fill="none"
                      />
                    </defs>
                    <text
                      font-family="'Segoe Script', 'Brush Script MT', cursive"
                      font-size="13"
                      fill="#C00012"
                      font-weight="600"
                    >
                      <textPath
                        :href="'#beprince-curve-' + (student.id || index)"
                        startOffset="2%"
                        text-anchor="start"
                      >
                        The Gateway of Education
                      </textPath>
                    </text>
                  </svg>

                  <div class="id-card__school-row">
                    <h2 class="id-card__school-name">{{ displaySchoolName }}</h2>
                    <span class="id-card__regd">Regd.</span>
                  </div>
                  <p class="id-card__subtitle">Nursery to Matric</p>
                </div>

                <div class="id-card__logo-wrap">
                  <img
                    :src="schoolLogoUrl"
                    alt="Be Prince Kids School"
                    class="id-card__logo"
                    @error="onLogoError"
                  />
                </div>
              </header>

              <div class="id-card__bottom">
                <!-- Footer red band — sits behind photo -->
                <footer class="id-card__footer">
                  <div class="id-card__footer-text">
                    <p class="id-card__footer-line">
                      {{ footerAddressLines.line1 }}
                    </p>
                    <p class="id-card__footer-line">
                      {{ footerAddressLines.line2 }}
                    </p>
                  </div>
                </footer>

                <!-- Body -->
                <div class="id-card__body">
                  <div class="id-card__fields">
                    <div class="id-card__field">
                      <span class="id-card__label">ID Card No. :</span>
                      <span class="id-card__value">{{
                        getIdCardNo(student)
                      }}</span>
                    </div>
                    <div class="id-card__field">
                      <span class="id-card__label">G. R. No. :</span>
                      <span class="id-card__value">{{
                        getStudentGr(student)
                      }}</span>
                    </div>
                    <div class="id-card__field">
                      <span class="id-card__label">Student Name :</span>
                      <span class="id-card__value">{{
                        studentDisplayName(student)
                      }}</span>
                    </div>
                    <div class="id-card__field">
                      <span class="id-card__label">Father Name :</span>
                      <span class="id-card__value">{{
                        studentFatherName(student)
                      }}</span>
                    </div>
                    <div class="id-card__field">
                      <span class="id-card__label">Class :</span>
                      <span class="id-card__value">{{
                        getStudentClassSection(student)
                      }}</span>
                    </div>
                    <div class="id-card__field">
                      <span class="id-card__label">Contact :</span>
                      <span class="id-card__value">{{
                        getStudentContact(student)
                      }}</span>
                    </div>
                  </div>

                  <div class="id-card__photo-wrap">
                    <div class="id-card__photo-frame">
                      <img
                        v-if="hasStudentPhoto(student)"
                        :src="studentPhotoUrl(student)"
                        :alt="studentDisplayName(student)"
                        class="id-card__photo"
                        referrerpolicy="no-referrer"
                        @error="handleImgError($event, student)"
                      />
                      <div v-else class="id-card__photo-placeholder">
                        <span>Photo</span>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
              </div>

              <!-- Back -->
              <div class="id-card id-card--back">
                <div class="id-card__top-line"></div>

                <div class="id-card__back-wrap">
                  <div class="id-card__back-body">
                    <div class="id-card__back-brand">
                      <img
                        :src="schoolLogoUrl"
                        alt="Be Prince Kids School"
                        class="id-card__back-logo"
                        @error="onLogoError"
                      />
                      <div class="id-card__school-row id-card__school-row--center">
                        <h2 class="id-card__school-name">{{ displaySchoolName }}</h2>
                        <span class="id-card__regd">Regd.</span>
                      </div>
                      <p class="id-card__subtitle">Nursery to Matric</p>
                    </div>

                    <p class="id-card__back-note">
                      If found, please return to:
                    </p>
                    <p class="id-card__back-address">{{ displayBackAddress }}</p>
                    <p v-if="displayCampusPhone" class="id-card__back-phone">
                      Ph: {{ displayCampusPhone }}
                    </p>

                    <div class="id-card__back-qr">
                      <qrcode-vue
                        :value="qrValue(student)"
                        :size="50"
                        level="H"
                        class="id-card__back-qr-code"
                      />
                      <p class="id-card__back-gr">
                        G.R. No. {{ getStudentGr(student) }}
                      </p>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { BButton, BSpinner, BBadge } from "bootstrap-vue";
import axios from "axios";
import QrcodeVue from "qrcode.vue";
import {
  getMediaPreviewUrl,
  handleDrivePreviewError,
} from "@/utils/gdriveUpload";

export default {
  name: "BeprinceIDCard",
  components: {
    BButton,
    BSpinner,
    BBadge,
    QrcodeVue,
  },
  data() {
    return {
      loading: false,
      error: null,
      reportItems: [],
      schoolName: "Be Prince Kids School",
      campusName: "",
      campusAddress: "",
      campusPhone: "",
      campusLogo: "",
      logoFailed: false,
      db: "",
      cID: "",
      classID: 0,
      sectionID: 0,
      status: "present",
      sessionID: 0,
      studentIDs: [],
      reportDomain: (this.$store.state.report || "").replace(/_/g, ""),
      defaultPlaceholder:
        "data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 100 120'%3E%3Crect width='100' height='120' fill='%23f5e6d3'/%3E%3Ccircle cx='50' cy='42' r='22' fill='%23c4a882'/%3E%3Cpath d='M10 120 Q10 82 50 82 Q90 82 90 120Z' fill='%23c4a882'/%3E%3C/svg%3E",
      defaultLogo:
        "data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 80 80'%3E%3Ccircle cx='40' cy='40' r='38' fill='none' stroke='%23000' stroke-width='2'/%3E%3Ccircle cx='40' cy='40' r='32' fill='none' stroke='%23000' stroke-width='1'/%3E%3Crect x='22' y='48' width='36' height='8' rx='1' fill='%23000'/%3E%3Cpath d='M26 48 L40 30 L54 48 Z' fill='none' stroke='%23000' stroke-width='1.5'/%3E%3Crect x='36' y='22' width='8' height='14' rx='1' fill='%23fff' stroke='%23000' stroke-width='1'/%3E%3Cellipse cx='40' cy='20' rx='4' ry='3' fill='%23ffcc00'/%3E%3C/svg%3E",
    };
  },
  computed: {
    displaySchoolName() {
      return (this.schoolName || "Be Prince Kids School").trim();
    },
    schoolLogoUrl() {
      if (this.logoFailed || !this.campusLogo) return this.defaultLogo;
      if (
        String(this.campusLogo).startsWith("blob:") ||
        String(this.campusLogo).startsWith("data:")
      ) {
        return this.campusLogo;
      }
      return getMediaPreviewUrl(this.campusLogo, "Image");
    },
    footerAddressLines() {
      const phone = this.campusPhone || "021-32216610";
      const raw = (this.campusAddress || "").trim();

      if (!raw) {
        return {
          line1: "R.B. 6/24, Rawra House, Yousuf Street, Gari Khata,",
          line2: `Arambagh, Karachi, Ph: ${phone}`,
        };
      }

      if (/ph\s*:/i.test(raw)) {
        const splitAt = raw.search(/,\s*Arambagh/i);
        if (splitAt !== -1) {
          return {
            line1: raw.slice(0, splitAt + 1).trim(),
            line2: raw.slice(splitAt + 1).trim().replace(/^,\s*/, ""),
          };
        }
        const phIndex = raw.search(/,\s*Ph\s*:/i);
        if (phIndex !== -1) {
          return {
            line1: raw.slice(0, phIndex + 1).trim(),
            line2: raw.slice(phIndex + 1).trim().replace(/^,\s*/, ""),
          };
        }
        return { line1: raw, line2: "" };
      }

      const gariSplit = raw.indexOf("Gari Khata,");
      if (gariSplit !== -1) {
        const line1 = raw.slice(0, gariSplit + "Gari Khata,".length).trim();
        const rest = raw.slice(gariSplit + "Gari Khata,".length).trim();
        return {
          line1,
          line2: rest ? `${rest}, Ph: ${phone}` : `Ph: ${phone}`,
        };
      }

      const parts = raw.split(",").map((p) => p.trim()).filter(Boolean);
      if (parts.length <= 1) {
        return { line1: raw, line2: `Ph: ${phone}` };
      }
      const mid = Math.ceil(parts.length / 2);
      return {
        line1: `${parts.slice(0, mid).join(", ")},`,
        line2: `${parts.slice(mid).join(", ")}, Ph: ${phone}`,
      };
    },
    displayBackAddress() {
      return (
        this.campusAddress ||
        "R.B. 6/24, Rawra House, Yousuf Street, Gari Khata, Arambagh, Karachi"
      ).trim();
    },
    displayCampusPhone() {
      return (this.campusPhone || "021-32216610").trim();
    },
    cardPages() {
      const pageSize = 3;
      const pages = [];
      const items = this.reportItems || [];
      for (let i = 0; i < items.length; i += pageSize) {
        pages.push(items.slice(i, i + pageSize));
      }
      return pages;
    },
  },
  created() {
    this.initReport();
  },
  methods: {
    onLogoError() {
      this.logoFailed = true;
    },
    initReport() {
      const query = this.$route.query;
      this.db = query.db || this.$store.state.userData?.db;
      this.cID = query.cID || 0;
      this.classID = query.cls || "";
      this.sectionID = query.sec || "";
      if (query.studentIDs) {
        this.studentIDs = query.studentIDs
          .split(",")
          .map((id) => parseInt(id, 10))
          .filter((id) => !isNaN(id));
      }
      this.status =
        this.studentIDs.length > 0 ? "all" : query.status || "present";
      this.sessionID = query.sessionID || 0;

      if (!this.db) {
        this.error = "System context missing. Please reload from dashboard.";
        return;
      }

      this.loadCampusInfo();
      this.loadReportData();
    },
    async loadCampusInfo() {
      try {
        const response = await axios.get(
          "https://apiv26.myskool.app/api/campuses",
          {
            params: { db: this.db, cId: this.cID },
            headers: {
              Authorization: `Bearer ${this.$store.state.userData?.token}`,
              accept: "application/json",
            },
          }
        );

        if (
          response.data?.status === "success" &&
          Array.isArray(response.data.data)
        ) {
          const match = response.data.data.find((c) => c.id == this.cID);
          if (match) {
            this.schoolName =
              match.organizationName ||
              match.orgName ||
              "Be Prince Kids School";
            this.campusName = match.branch || match.name || "";
            this.campusAddress = match.address || match.branch || "";
            this.campusPhone =
              match.phone || match.contact || match.mobile || "";
            this.campusLogo =
              match.logo || match.image || match.branchLogo || "";
          }
        }
      } catch (e) {
        console.warn("Campus branding failed to load", e);
      }
    },
    normalizeStudentList(responseData) {
      if (!responseData) return [];
      if (!Array.isArray(responseData) && Array.isArray(responseData.data)) {
        return responseData.data;
      }
      if (Array.isArray(responseData)) return responseData;
      return [];
    },
    async loadReportData() {
      this.loading = true;
      this.error = null;

      const headers = {
        Authorization: `Bearer ${this.$store.state.userData?.token}`,
        accept: "application/json",
      };
      const sessionID = this.sessionID || this.$store.state.sessionID || 0;

      try {
        let response;
        if (this.studentIDs.length > 0) {
          response = await axios.get(
            `${this.$store.state.domain}Students/LoadDataSelected`,
            {
              params: {
                db: this.db,
                cID: this.cID,
                ids: this.studentIDs.join(","),
                status: "all",
                sessionID,
              },
              headers,
            }
          );
        } else {
          response = await axios.get(
            `${this.$store.state.domain}Students/LoadDataAll`,
            {
              params: {
                db: this.db,
                cID: this.cID,
                clsID: this.classID || 0,
                section: this.sectionID || 0,
                status: this.status,
                sessionID,
              },
              headers,
            }
          );
        }

        if (response.data?.status === "success") {
          this.reportItems = this.normalizeStudentList(response.data.data);
        } else if (Array.isArray(response.data)) {
          this.reportItems = response.data;
        } else {
          this.error =
            response.data?.message ||
            "The server did not return valid student records.";
        }
      } catch (err) {
        console.error("Be Prince ID card error:", err);
        this.error = "Unable to connect to school records system.";
      } finally {
        this.loading = false;
      }
    },
    getIdCardNo(student) {
      return student?.id || student?.studentID || student?.cardNo || "—";
    },
    getStudentGr(student) {
      return student?.grNo || student?.gr || student?.gr_no || "—";
    },
    getStudentClassSection(student) {
      if (!student) return "—";
      const cls =
        student.className ||
        student.cls ||
        student.meta_class ||
        "";
      const sec =
        student.sectionName ||
        student.section ||
        student.secName ||
        student.meta_section ||
        "";

      if (cls && sec) {
        const clsPart = String(cls).split(" - ")[0].trim();
        return `${clsPart} - ${sec}`;
      }
      return cls || sec || "—";
    },
    getStudentContact(student) {
      if (!student) return "—";

      const fields = [
        student.contact_father,
        student.contact_student,
        student.contact_home,
        student.father_mobile,
        student.fatherMobile,
        student.fatherContact,
        student.mobile,
        student.phone,
        student.contact,
        student.contact_number,
        student.whatsapp,
        student.whatsapp_no,
        student.emergency_contact,
        student.contact_mother,
        student.mother_mobile,
        student.motherMobile,
        student.guardianContact,
        student.parentContact,
      ];

      for (const value of fields) {
        const contact = String(value ?? "").trim();
        if (contact) return contact;
      }

      return "—";
    },
    studentDisplayName(student) {
      const name =
        student?.name || student?.studentName || student?.student_name || "";
      return String(name).trim().toUpperCase() || "—";
    },
    studentFatherName(student) {
      const name =
        student?.father_name || student?.fatherName || student?.father || "";
      return String(name).trim().toUpperCase() || "—";
    },
    hasStudentPhoto(student) {
      const url = student?.picture || student?.photo || student?.image || "";
      return !!url && url !== this.defaultPlaceholder;
    },
    studentPhotoUrl(student) {
      const url = student?.picture || student?.photo || student?.image || "";
      if (!url) return this.defaultPlaceholder;
      if (String(url).startsWith("blob:")) return url;
      return getMediaPreviewUrl(url, "Image");
    },
    handleImgError(e, student) {
      const url = student?.picture || student?.photo || student?.image || "";
      if (url && !String(url).startsWith("blob:")) {
        handleDrivePreviewError(e, url, "image");
        return;
      }
      e.target.src = this.defaultPlaceholder;
    },
    qrValue(student) {
      const gr = this.getStudentGr(student);
      const id = this.getIdCardNo(student);
      return `BP-${gr !== "—" ? gr : id}`;
    },
    print() {
      window.print();
    },
    close() {
      if (this.studentIDs?.length > 0) {
        this.$router.push({ name: "manage-studentgrid" });
      } else {
        this.$router.push({ name: "report" });
      }
    },
  },
};
</script>

<style lang="scss" scoped>
@import url("https://fonts.googleapis.com/css2?family=Roboto:wght@400;700;900&display=swap");

$bp-red: #c00012;
$bp-red-dark: #a0000f;
$label-dark: #222222;
$card-w: 10.2cm;
$card-h: 6.4cm;

.beprince-id-card-report {
  background: #eef1f5;
  min-height: 100vh;
  padding: 2rem;
  font-family: "Roboto", "Arial", sans-serif;
  color: $label-dark;
}

.control-bar {
  background: #fff;
  padding: 1rem 1.5rem;
  margin-bottom: 2rem;
  border-radius: 14px;
  box-shadow: 0 6px 24px rgba(0, 0, 0, 0.08);
  display: flex;
  justify-content: space-between;
  align-items: center;
  position: sticky;
  top: 1rem;
  z-index: 100;

  .brand-badge {
    background: linear-gradient(135deg, $bp-red 0%, $bp-red-dark 100%);
    padding: 8px;
    border-radius: 10px;
  }

  .control-bar__accent {
    color: $bp-red;
  }

  .control-bar__count-badge {
    background: rgba(192, 0, 18, 0.12) !important;
    color: $bp-red !important;
    font-weight: 700;
  }
}

.action-buttons {
  display: flex;
  gap: 10px;
}

.btn-modern {
  display: inline-flex;
  align-items: center;
  padding: 0.65rem 1.4rem;
  border-radius: 10px;
  font-weight: 700;
  cursor: pointer;
  border: none;
  font-size: 0.88rem;
}

.btn-print {
  background: linear-gradient(135deg, $bp-red 0%, $bp-red-dark 100%);
  color: #fff;
}

.btn-close {
  background: #fff;
  color: #555;
  border: 1px solid #dcdde1;
}

.state-wrapper {
  background: #fff;
  border-radius: 16px;
  padding: 3rem;
  box-shadow: 0 8px 30px rgba(0, 0, 0, 0.06);
}

.print-pages {
  width: 100%;
}

.print-page + .print-page {
  margin-top: 28px;
}

.cards-grid {
  display: flex;
  flex-wrap: wrap;
  justify-content: center;
  gap: 14px 18px;
  width: 100%;
}

.student-id-block {
  display: flex;
  flex-direction: row;
  align-items: flex-start;
  justify-content: center;
  gap: 12px;
  break-inside: avoid;
  page-break-inside: avoid;
}

/* ─── ID Card ─── */
.id-card {
  width: $card-w;
  height: $card-h;
  background: #fff;
  border: 1px solid rgba(0, 0, 0, 0.12);
  border-radius: 4px;
  box-shadow: 0 6px 20px rgba(0, 0, 0, 0.1);
  box-sizing: border-box;
  display: flex;
  flex-direction: column;
  overflow: hidden;
  position: relative;
  break-inside: avoid;
  page-break-inside: avoid;
}

.id-card__top-line {
  height: 0.42cm;
  min-height: 10px;
  background: $bp-red;
  flex-shrink: 0;
  margin: 0;
  border-radius: 3px 3px 0 0;
}

.id-card__header {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  padding: 3px 10px 0 12px;
  flex-shrink: 0;
  gap: 6px;
}

.id-card__header-left {
  flex: 1;
  min-width: 0;
}

.id-card__tagline {
  width: 100%;
  max-width: 200px;
  height: 22px;
  display: block;
  margin-bottom: -2px;
}

.id-card__school-row {
  display: flex;
  align-items: flex-start;
  gap: 4px;
  flex-wrap: nowrap;

  &--center {
    justify-content: center;
  }
}

.id-card__school-name {
  margin: 0;
  font-size: 15pt;
  font-weight: 900;
  color: $bp-red;
  line-height: 1.05;
  letter-spacing: -0.01em;
  white-space: nowrap;
}

.id-card--front {
  .id-card__header {
    display: grid;
    grid-template-columns: minmax(0, 1fr) 1.72cm;
    gap: 0;
    padding: 3px 6px 0 10px;
    align-items: start;
  }

  .id-card__header-left {
    min-width: 0;
    width: 100%;
    max-width: 100%;
  }

  .id-card__tagline {
    width: auto;
    max-width: 68%;
    margin-left: 0;
    margin-bottom: -2px;
  }

  .id-card__school-row {
    display: flex;
    width: 100%;
    max-width: 100%;
    gap: 3px;
    align-items: flex-start;
    flex-wrap: nowrap;
  }

  .id-card__school-name {
    font-size: 18pt;
    font-weight: 600;
    letter-spacing: 0.045em;
    flex: 0 1 auto;
    min-width: 0;
    line-height: 1.05;
  }

  .id-card__regd {
    font-size: 6.5pt;
    font-weight: 700;
    margin: 2px 0 0;
    flex-shrink: 0;
    align-self: flex-start;
    line-height: 1;
  }

  .id-card__logo-wrap {
    width: 1.72cm;
    height: 1.72cm;
    margin-top: 0;
    margin-left: -4px;
    justify-self: start;
  }

  .id-card__subtitle {
    padding-left: 0.22cm;
  }
}

.id-card--back {
  .id-card__school-name {
    font-size: 14.5pt;
    font-weight: 600;
    letter-spacing: 0.03em;
    flex: none;
  }

  .id-card__regd {
    font-size: 6.5pt;
    margin-top: 2px;
    margin-bottom: 0;
  }

  .id-card__subtitle {
    font-size: 8pt;
    font-weight: 700;
  }

  .id-card__back-logo {
    width: 1.15cm;
    height: 1.15cm;
    margin-bottom: 2px;
  }
}

.id-card__regd {
  font-size: 7pt;
  font-weight: 700;
  color: $bp-red;
  margin-top: 2px;
  flex-shrink: 0;
}

.id-card__subtitle {
  margin: 0;
  font-size: 11.5pt;
  font-weight: 900;
  color: $label-dark;
  line-height: 1.1;
}

.id-card__logo-wrap {
  flex-shrink: 0;
  width: 1.55cm;
  height: 1.55cm;
  display: flex;
  align-items: center;
  justify-content: center;
}

.id-card__logo {
  width: 100%;
  height: 100%;
  object-fit: contain;
  display: block;
}

.id-card__bottom {
  flex: 1;
  position: relative;
  min-height: 0;
  padding-bottom: 0.92cm;
}

.id-card__body {
  height: 100%;
  position: relative;
  z-index: 2;
  display: flex;
  align-items: flex-end;
  padding: 2px 10px 0 12px;
  gap: 10px;
  min-height: 0;
  box-sizing: border-box;
}

.id-card__fields {
  flex: 1;
  display: flex;
  flex-direction: column;
  justify-content: flex-start;
  align-self: flex-start;
  gap: 1px;
  min-width: 4.8cm;
  max-width: calc(100% - 2.45cm);
  overflow: visible;
  padding-top: 0.48cm;
  padding-left: 0.42cm;
  padding-bottom: 0;
}

.id-card__field {
  display: grid;
  grid-template-columns: 2.05cm minmax(0, 1fr);
  column-gap: 2px;
  align-items: baseline;
  line-height: 1.35;
  font-size: 8.2pt;
  white-space: nowrap;
}

.id-card__label {
  font-weight: 700;
  color: $label-dark;
  white-space: nowrap;
  text-align: right;
  padding-right: 2px;
}

.id-card__value {
  font-weight: 700;
  color: $bp-red;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.id-card__photo-wrap {
  flex-shrink: 0;
  position: relative;
  z-index: 3;
  display: flex;
  align-items: flex-end;
  justify-content: flex-end;
  margin-bottom: -0.22cm;
}

.id-card__photo-frame {
  width: 2.3cm;
  height: 3.22cm;
  border: 2px solid #000;
  border-radius: 12px;
  overflow: hidden;
  background: #f5e6d3;
  box-sizing: border-box;
  box-shadow: 0 0 0 1px #fff;
}

.id-card__photo {
  width: 100%;
  height: 100%;
  object-fit: cover;
  display: block;
}

.id-card__photo-placeholder {
  width: 100%;
  height: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 7pt;
  font-weight: 700;
  color: #888;
  text-transform: uppercase;
}

.id-card__footer {
  position: absolute;
  left: 0;
  right: 0;
  bottom: 0;
  min-height: 0.92cm;
  background: $bp-red;
  z-index: 1;
  border-radius: 0 0 3px 3px;
  display: flex;
  align-items: flex-start;
  padding: 3px 10px 4px 12px;
  box-sizing: border-box;
  -webkit-print-color-adjust: exact;
  print-color-adjust: exact;
}

.id-card__footer-text {
  width: 100%;
  max-width: calc(100% - 2.35cm);
  text-align: left;
}

.id-card__footer-line {
  margin: 0;
  font-size: 6.6pt;
  font-weight: 700;
  line-height: 1.2;
  letter-spacing: 0.01em;
  color: #fff;
}

/* ─── Back Side ─── */
.id-card__back-wrap {
  flex: 1;
  position: relative;
  min-height: 0;
  display: flex;
  flex-direction: column;
}

.id-card__back-body {
  flex: 1;
  height: 100%;
  position: relative;
  z-index: 2;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: flex-start;
  text-align: center;
  padding: 2px 12px 8px;
  box-sizing: border-box;
}

.id-card__back-brand {
  display: flex;
  flex-direction: column;
  align-items: center;
  margin-bottom: 2px;
}

.id-card__back-logo {
  width: 1.15cm;
  height: 1.15cm;
  object-fit: contain;
  display: block;
  margin-bottom: 2px;
}

.id-card__back-note {
  margin: 3px 0 2px;
  font-size: 7pt;
  font-weight: 700;
  color: $label-dark;
}

.id-card__back-address {
  margin: 0;
  font-size: 6.2pt;
  font-weight: 700;
  color: $label-dark;
  line-height: 1.25;
  max-width: 92%;
}

.id-card__back-phone {
  margin: 2px 0 0;
  font-size: 6.8pt;
  font-weight: 700;
  color: $bp-red;
}

.id-card__back-qr {
  margin-top: auto;
  padding-top: 4px;
  padding-bottom: 2px;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 2px;
}

.id-card__back-qr-code {
  display: block;
}

.id-card__back-gr {
  margin: 0;
  font-size: 6.5pt;
  font-weight: 700;
  color: $label-dark;
}

@media print {
  .no-print {
    display: none !important;
  }

  @page {
    size: A4 landscape;
    margin: 6mm;
  }

  html,
  body,
  #app {
    height: auto !important;
    min-height: 0 !important;
    overflow: visible !important;
    background: #fff !important;
  }

  .beprince-id-card-report {
    padding: 0 !important;
    background: #fff !important;
    min-height: 0 !important;
  }

  .print-page:not(:last-child) {
    page-break-after: always;
    break-after: page;
  }

  .cards-grid {
    gap: 3mm 4mm !important;
  }

  .student-id-block {
    display: flex !important;
    flex-direction: row !important;
    align-items: flex-start !important;
    gap: 3mm !important;
    page-break-inside: avoid !important;
    break-inside: avoid !important;
  }

  .id-card {
    width: $card-w !important;
    height: $card-h !important;
    box-shadow: none !important;
    border: 0.5pt solid #ccc !important;
    -webkit-print-color-adjust: exact !important;
    print-color-adjust: exact !important;
  }

  .id-card__top-line,
  .id-card__footer,
  .id-card__value {
    -webkit-print-color-adjust: exact !important;
    print-color-adjust: exact !important;
  }
}
</style>

<style lang="scss">
@media print {
  html,
  body,
  #app,
  #app.h-100 {
    height: auto !important;
    min-height: 0 !important;
    overflow: visible !important;
  }
}
</style>
